import { Box } from "@material-ui/core";
import clsx from "clsx";
import {
  PolicyDetailsModal,
  PolicyModalButton,
  UserPreferencesAppliedBanner,
} from "halifax";
import React, { useContext } from "react";
import {
  CLEAR_ALL_FILTERS,
  IIdLodgings,
  ModalScreens,
  POLICY_MODAL,
  VIEWED_POLICY_MODAL,
} from "redmond";
import { ClientContext } from "../../../../App";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import {
  AVAILABLE,
  CONTROL,
  CUSTOMER_PROFILE_EXPERIMENT,
  CUSTOMER_PROFILE_VARIANTS,
  STAYS_SEARCH,
  addTrackingProperties,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  useExperiments,
} from "../../../../context/experiments";
import {
  PATH_AVAILABILITY,
  PATH_STAYS_AVAILABILITY,
} from "../../../../utils/paths";
import { HotelSearchControl } from "../../../search/components/HotelSearchControl";
import { HotelSearchButton } from "../../../search/components/HotelSearchControl/components/SearchButton";
import { transformToStringifiedAvailabilityQuery } from "../../../shop/utils/queryStringHelpers";
import { AvailabilityFilter } from "../AvailabilityFilter";
import { AvailabilitySearchControlConnectorProps } from "./container";
import "./styles.scss";
import * as textConstants from "./textConstants";

export interface IAvailabilitySearchControlProps
  extends AvailabilitySearchControlConnectorProps {
  displaySearchOnChange?: boolean;
}

export const AvailabilitySearchControl = (
  props: IAvailabilitySearchControlProps
) => {
  const {
    fetchInitialHotelAvailability,
    location,
    fromDate,
    untilDate,
    adultsCount,
    children,
    displaySearchOnChange = true,
    mapBounds,
    setMapBound,
    setViewHotelsNearLocation,
    areRequestParametersChanged,
    resetFilters,
    roomsCount,
    petsCount,
    shouldApplyUserHotelPreferences,
  } = props;

  const expState = useExperiments();

  const isCustomerProfileExperiment =
    getExperimentVariantCustomVariants(
      expState.experiments,
      CUSTOMER_PROFILE_EXPERIMENT,
      CUSTOMER_PROFILE_VARIANTS
    ) !== CONTROL;

  const staysSearchEnabled =
    getExperimentVariant(expState.experiments, STAYS_SEARCH) === AVAILABLE;

  const [isPolicyModalOpen, setIsPolicyModalOpen] = React.useState(false);

  const { policies } = useContext(ClientContext);

  const onShowPolicyDetailsModal = () => {
    setIsPolicyModalOpen(true);
    trackEvent({
      eventName: VIEWED_POLICY_MODAL,
      properties: {
        type: POLICY_MODAL,
        entry_point: ModalScreens.HOTELS_AVAILABILITY,
        funnel: "hotels",
      },
    });
  };

  return (
    <Box className="hotel-availability-search-container">
      <Box
        className={clsx("hotel-availability-search-control", "show-policy-button")}
      >
        <Box className="search-components-section">
          <HotelSearchControl
            onSearch={fetchInitialHotelAvailability}
            displaySearchOnChange
            showTravelerSelection
            showSearchButton={false}
            hideCalendarSeparator
            showTotalTravelers
          />
        </Box>
        <Box className="sort-filter-section">
          {(!displaySearchOnChange || areRequestParametersChanged) && (
            <HotelSearchButton
              className={clsx("hotel-search-control-button", "b2b")}
              message={textConstants.SEARCH_AGAIN}
              onClick={(history) => {
                const path = staysSearchEnabled
                  ? PATH_STAYS_AVAILABILITY
                  : PATH_AVAILABILITY;
                history.push(
                  `${path}${transformToStringifiedAvailabilityQuery(
                    (location?.id as IIdLodgings).lodgingSelection.searchTerm,
                    fromDate,
                    untilDate,
                    adultsCount,
                    children,
                    roomsCount,
                    petsCount
                  )}`
                );

                mapBounds && setMapBound(null);
                setViewHotelsNearLocation(null);
                resetFilters();
                fetchInitialHotelAvailability(
                  history,
                  false,
                  false,
                  staysSearchEnabled
                );
              }}
            />
          )}
          <AvailabilityFilter />
          {isCustomerProfileExperiment && shouldApplyUserHotelPreferences && (
            <UserPreferencesAppliedBanner
              resetFilters={() => {
                resetFilters();

                trackEvent({
                  eventName: CLEAR_ALL_FILTERS,
                  properties: addTrackingProperties(
                    expState.trackingProperties
                  ),
                });
              }}
              type="hotel"
            />
          )}
        </Box>
      </Box>
      <Box className="policy-modal-button-container">
        <PolicyModalButton
          underline={false}
          policies={policies}
          onClick={onShowPolicyDetailsModal}
        />
      </Box>
      <PolicyDetailsModal
        policies={policies}
        isOpen={isPolicyModalOpen}
        setIsOpen={setIsPolicyModalOpen}
        productType="hotel"
      />
    </Box>
  );
};
